import React, { useState, useEffect, useCallback } from 'react';
import AdminSidebar from '../Sidebar/AdminSidebar';
import AdminNavbar from '../Navbars/AdminNavbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { Oval } from 'react-loader-spinner';
import Select from 'react-select';
import {
    ArrowDown, ArrowUp, Plus, Trash2, Save, X, Wallet,
    AlertTriangle, Users, ListOrdered,
} from 'lucide-react';

interface Approver { user_id?: number | null; email: string; name?: string | null }
interface Level {
    id: number;
    name: string;
    position: number;
    is_disbursement: boolean;
    is_active: boolean;
    description?: string | null;
    approvers: Approver[];
}

const emptyDraft = { name: '', description: '', is_disbursement: false, is_active: true, approvers: [] as Approver[] };

function VoucherSettings() {
    const [levels, setLevels] = useState<Level[]>([]);
    const [staff, setStaff] = useState<any[]>([]);
    const [loader, setLoader] = useState(true);
    const [saving, setSaving] = useState<number | 'new' | null>(null);

    // Per-level edit buffers, keyed by level id.
    const [edits, setEdits] = useState<Record<number, any>>({});
    const [showCreate, setShowCreate] = useState(false);
    const [draft, setDraft] = useState<any>(emptyDraft);

    const staffOptions = React.useMemo(
        () => staff.map((s: any) => ({
            value: s.email,
            label: `${s.name} — ${s.role}${s.branch ? ' · ' + s.branch : ''} (${s.email})`,
            data: s,
        })),
        [staff]
    );

    const load = useCallback(async () => {
        try {
            const [levelRes, staffRes] = await Promise.all([
                SuperAdminApis.getVoucherLevels(),
                SuperAdminApis.getVoucherApproverCandidates(),
            ]);

            if (levelRes?.data?.status) {
                const list: Level[] = levelRes.data.data || [];
                setLevels(list);
                setEdits(Object.fromEntries(list.map((l) => [l.id, {
                    name: l.name,
                    description: l.description || '',
                    is_disbursement: l.is_disbursement,
                    is_active: l.is_active,
                    approvers: l.approvers || [],
                }])));
            }
            if (staffRes?.data?.status) setStaff(staffRes.data.data || []);
        } catch (error) {
            toast.error('Could not load the approval chain');
        } finally {
            setLoader(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const setEdit = (id: number, key: string, value: any) =>
        setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));

    const approversToOptions = (approvers: Approver[]) =>
        (approvers || []).map((a) => ({
            value: a.email,
            label: staffOptions.find((o) => o.value === a.email)?.label || a.email,
        }));

    const optionsToApprovers = (selected: any): Approver[] =>
        (selected || []).map((opt: any) => {
            const match = staff.find((s: any) => s.email === opt.value);
            return { user_id: match?.id ?? null, email: opt.value, name: match?.name ?? null };
        });

    const saveLevel = async (level: Level) => {
        const edit = edits[level.id];
        if (!edit?.name?.trim()) {
            toast.warn('A level needs a name');
            return;
        }

        setSaving(level.id);
        try {
            const response: any = await SuperAdminApis.updateVoucherLevel(level.id, edit);
            if (response?.data?.status) {
                toast.success(`${edit.name} saved`);
                load();
            } else {
                toast.error(response?.data?.message || 'Could not save the level');
            }
        } catch (error: any) {
            toast.error(error?.response?.data?.message || 'Could not save the level');
        } finally {
            setSaving(null);
        }
    };

    const createLevel = async () => {
        if (!draft.name?.trim()) {
            toast.warn('Give the new level a name');
            return;
        }

        setSaving('new');
        try {
            const response: any = await SuperAdminApis.createVoucherLevel(draft);
            if (response?.data?.status) {
                toast.success('Approval level added');
                setDraft(emptyDraft);
                setShowCreate(false);
                load();
            } else {
                toast.error(response?.data?.message || 'Could not add the level');
            }
        } catch (error: any) {
            toast.error(error?.response?.data?.message || 'Could not add the level');
        } finally {
            setSaving(null);
        }
    };

    const removeLevel = async (level: Level) => {
        if (!window.confirm(`Remove the "${level.name}" level? Vouchers already in flight keep the chain they started with.`)) return;

        try {
            const response: any = await SuperAdminApis.deleteVoucherLevel(level.id);
            if (response?.data?.status) {
                toast.success('Level removed');
                load();
            } else {
                toast.error(response?.data?.message || 'Could not remove the level');
            }
        } catch (error: any) {
            toast.error(error?.response?.data?.message || 'Could not remove the level');
        }
    };

    /** Moves a level up or down and persists the whole running order. */
    const move = async (index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (target < 0 || target >= levels.length) return;

        const reordered = [...levels];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        setLevels(reordered); // optimistic

        try {
            const response: any = await SuperAdminApis.reorderVoucherLevels(reordered.map((l) => l.id));
            if (!response?.data?.status) {
                toast.error('Could not save the new order');
                load();
            }
        } catch (error) {
            toast.error('Could not save the new order');
            load();
        }
    };

    const activeChain = levels.filter((l) => l.is_active);

    return (
        <>
            <AdminSidebar title="Voucher Settings" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Voucher Settings" />
                <ToastContainer />
                <div className="lg:px-10 pb-10 px-6 bg-[#FAFAFA] min-h-screen">

                    <div className="pt-6 pb-4 flex flex-wrap gap-3 justify-between items-start">
                        <div>
                            <h1 className="text-[20px] font-[700] text-[#333333] flex items-center gap-2">
                                <ListOrdered className="w-5 h-5 text-[#E04343]" />
                                Expense Voucher Approval Chain
                            </h1>
                            <p className="text-[13px] text-[#808080] mt-1 max-w-3xl">
                                Every expense voucher travels through these levels in the order shown. Add, rename,
                                reorder or switch off a level, and choose who may act on it — any one of a level's
                                approvers can approve or reject. Vouchers already in flight keep the chain they
                                started with, so changes here never rewrite work in progress.
                            </p>
                        </div>
                        <button
                            onClick={() => setShowCreate((s) => !s)}
                            className="flex items-center gap-1.5 bg-[#E04343] text-white text-[13px] px-3 py-2 rounded-md hover:bg-[#c93a3a]"
                        >
                            <Plus className="w-4 h-4" /> Add Level
                        </button>
                    </div>

                    {loader ? (
                        <div className="flex justify-center py-20">
                            <Oval visible height="50" width="50" color="#E04343" secondaryColor="#FCBF94" ariaLabel="loading" />
                        </div>
                    ) : (
                        <>
                            {/* Chain preview */}
                            <div className="bg-white rounded-[10px] p-4 shadow-sm border mb-5">
                                <p className="text-[11px] font-[600] text-[#808080] uppercase tracking-wide mb-2">
                                    Current chain
                                </p>
                                {activeChain.length === 0 ? (
                                    <p className="text-[13px] text-red-600">
                                        No active levels — new vouchers will have no approval chain at all.
                                    </p>
                                ) : (
                                    <div className="flex flex-wrap items-center gap-2">
                                        {activeChain.map((level, i) => (
                                            <React.Fragment key={level.id}>
                                                <span className={`text-[12px] px-3 py-1.5 rounded-full border ${level.is_disbursement ? 'bg-green-50 border-green-200 text-green-800' : 'bg-gray-50 border-gray-200 text-[#333333]'}`}>
                                                    {level.name}
                                                    {level.is_disbursement && ' · disburses'}
                                                    {level.approvers?.length === 0 && ' ⚠'}
                                                </span>
                                                {i < activeChain.length - 1 && <span className="text-[#B0B0B0]">→</span>}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                )}
                                {activeChain.some((l) => !l.approvers?.length) && (
                                    <p className="text-[12px] text-amber-700 mt-2 flex items-center gap-1">
                                        <AlertTriangle className="w-3.5 h-3.5" />
                                        Levels marked ⚠ have no approver assigned — nobody but an admin can act on them.
                                    </p>
                                )}
                            </div>

                            {/* New level */}
                            {showCreate && (
                                <div className="bg-white rounded-[10px] p-4 shadow-sm border-2 border-[#E04343] mb-5">
                                    <div className="flex justify-between items-center mb-3">
                                        <h3 className="text-[14px] font-[700]">New Approval Level</h3>
                                        <button onClick={() => { setShowCreate(false); setDraft(emptyDraft); }}>
                                            <X className="w-4 h-4 text-[#808080]" />
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-[11px] font-[600] text-[#808080] mb-1">Level name</label>
                                            <input
                                                value={draft.name}
                                                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                                                placeholder="e.g. Executive Director"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-[13px]"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-[600] text-[#808080] mb-1">Description</label>
                                            <input
                                                value={draft.description}
                                                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                                                placeholder="What this level checks (optional)"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-[13px]"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-[11px] font-[600] text-[#808080] mb-1">Approvers</label>
                                            <Select
                                                isMulti
                                                options={staffOptions}
                                                value={approversToOptions(draft.approvers)}
                                                onChange={(selected) => setDraft({ ...draft, approvers: optionsToApprovers(selected) })}
                                                placeholder="Search staff by name, email or role…"
                                                classNamePrefix="select"
                                            />
                                        </div>
                                        <label className="flex items-center gap-2 text-[13px]">
                                            <input
                                                type="checkbox"
                                                checked={draft.is_disbursement}
                                                onChange={(e) => setDraft({ ...draft, is_disbursement: e.target.checked })}
                                                className="w-4 h-4 accent-[#E04343]"
                                            />
                                            This level releases the money (can set the approved amount)
                                        </label>
                                    </div>
                                    <div className="flex justify-end mt-4">
                                        <button
                                            onClick={createLevel}
                                            disabled={saving === 'new'}
                                            className="flex items-center gap-2 bg-[#E04343] text-white text-[13px] px-4 py-2 rounded-md disabled:opacity-60"
                                        >
                                            <Plus className="w-4 h-4" /> Add Level
                                            {saving === 'new' && <Oval visible height="14" width="14" color="#fff" secondaryColor="#f9adad" ariaLabel="saving" />}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Levels */}
                            <div className="space-y-4">
                                {levels.map((level, index) => {
                                    const edit = edits[level.id] || {};
                                    return (
                                        <div key={level.id} className={`bg-white rounded-[10px] p-4 shadow-sm border ${edit.is_active ? '' : 'opacity-60'}`}>
                                            <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
                                                <div className="flex items-center gap-3">
                                                    <span className="w-7 h-7 rounded-full bg-[#19213D] text-white text-[12px] font-[700] flex items-center justify-center">
                                                        {index + 1}
                                                    </span>
                                                    <div>
                                                        <h3 className="text-[14px] font-[700] text-[#333333]">{level.name}</h3>
                                                        <p className="text-[11px] text-[#808080] flex items-center gap-1">
                                                            <Users className="w-3 h-3" />
                                                            {level.approvers?.length || 0} approver{level.approvers?.length === 1 ? '' : 's'}
                                                            {level.is_disbursement && (
                                                                <span className="ml-2 text-green-700 flex items-center gap-1">
                                                                    <Wallet className="w-3 h-3" /> disburses
                                                                </span>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-1">
                                                    <button onClick={() => move(index, -1)} disabled={index === 0}
                                                        title="Move up"
                                                        className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30">
                                                        <ArrowUp className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button onClick={() => move(index, 1)} disabled={index === levels.length - 1}
                                                        title="Move down"
                                                        className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30">
                                                        <ArrowDown className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button onClick={() => removeLevel(level)}
                                                        title="Remove level"
                                                        className="p-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50">
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-[11px] font-[600] text-[#808080] mb-1">Level name</label>
                                                    <input
                                                        value={edit.name ?? ''}
                                                        onChange={(e) => setEdit(level.id, 'name', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-[13px]"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-[600] text-[#808080] mb-1">Description</label>
                                                    <input
                                                        value={edit.description ?? ''}
                                                        onChange={(e) => setEdit(level.id, 'description', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-[13px]"
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-[11px] font-[600] text-[#808080] mb-1">
                                                        Approvers — any one of them can approve or reject
                                                    </label>
                                                    <Select
                                                        isMulti
                                                        options={staffOptions}
                                                        value={approversToOptions(edit.approvers || [])}
                                                        onChange={(selected) => setEdit(level.id, 'approvers', optionsToApprovers(selected))}
                                                        placeholder="Search staff by name, email or role…"
                                                        classNamePrefix="select"
                                                    />
                                                </div>
                                            </div>

                                            <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
                                                <div className="flex flex-wrap gap-4">
                                                    <label className="flex items-center gap-2 text-[12px]">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!edit.is_active}
                                                            onChange={(e) => setEdit(level.id, 'is_active', e.target.checked)}
                                                            className="w-4 h-4 accent-[#E04343]"
                                                        />
                                                        Active in the chain
                                                    </label>
                                                    <label className="flex items-center gap-2 text-[12px]">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!edit.is_disbursement}
                                                            onChange={(e) => setEdit(level.id, 'is_disbursement', e.target.checked)}
                                                            className="w-4 h-4 accent-[#E04343]"
                                                        />
                                                        Releases the money
                                                    </label>
                                                </div>

                                                <button
                                                    onClick={() => saveLevel(level)}
                                                    disabled={saving === level.id}
                                                    className="flex items-center gap-2 bg-[#19213D] text-white text-[13px] px-4 py-2 rounded-md hover:bg-[#2A3352] disabled:opacity-60"
                                                >
                                                    <Save className="w-4 h-4" /> Save
                                                    {saving === level.id && <Oval visible height="14" width="14" color="#fff" secondaryColor="#9aa" ariaLabel="saving" />}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}

                                {levels.length === 0 && (
                                    <div className="bg-white rounded-[10px] p-10 text-center border">
                                        <p className="text-[14px] text-[#808080]">
                                            No approval levels yet. Add the first one to start building the chain.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default VoucherSettings;
